import { ChevronRight, CloudUpload, Laptop } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { formatDate } from "@/lib/format";
import { addedName, PIPELINE_STATE, useAddSource } from "@/lib/useAddSource";

/**
 * CAP-1/2/3 and CAP-7 on a phone — one page that takes a file or a typed note, because
 * uploading from a phone is how a recording gets in.
 *
 * **One picker, not three routes.** The desktop's full mode has a route per kind and the
 * route decides what the Server is told; a phone has a file picker that offers everything
 * the device holds, so the *file* decides. That rule, its closed extension list and its
 * refusal wording live in `useAddSource`, which simple mode's Add a source (UI-16) renders
 * from too — the two reduced surfaces make the same promise about what is accepted, and
 * one of them must not be able to start offering something the other refuses.
 */
export function PhoneAdd() {
  const { recentSources, recordings } = useApp();
  const add = useAddSource();

  const picker = useRef<HTMLInputElement>(null);
  const uploading = add.progress !== null;

  return (
    <div className="flex flex-col gap-5 p-4 [animation:omFadeUp_220ms_cubic-bezier(0.4,0,0.2,1)_both]">
      <div className="flex flex-col items-center gap-3 rounded-[var(--radius-xl)] border-[1.5px] border-dashed border-[var(--border-strong)] bg-[var(--surface-glass)] px-[18px] py-7 text-center">
        <CloudUpload size={28} strokeWidth={2} className="text-[var(--vl-bridge)]" aria-hidden />
        <p className="text-[16px] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Pick a recording or document
        </p>
        <p className="text-[13px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
          It uploads in the background, then the worker reads it.
        </p>

        <input
          ref={picker}
          type="file"
          className="hidden"
          accept={add.accepted.join(",")}
          onChange={(event) => {
            const chosen = event.target.files?.[0] ?? null;
            // Cleared so choosing the same file twice fires a second change event, which
            // is the only way a retry after a failure works.
            event.target.value = "";
            add.send(chosen);
          }}
        />

        <button
          type="button"
          disabled={uploading}
          onClick={() => picker.current?.click()}
          className="h-12 w-full rounded-[var(--radius-md)] bg-[var(--primary)] text-[15px] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
        >
          {uploading ? `Uploading… ${Math.round((add.progress ?? 0) * 100)}%` : "Choose a file"}
        </button>

        {uploading && (
          <>
            <div className="h-1 w-full overflow-hidden rounded-[var(--radius-full)] bg-[var(--surface-2)]">
              <div
                className="h-full bg-[var(--vl-bridge)] transition-[width]"
                style={{ width: `${Math.round((add.progress ?? 0) * 100)}%` }}
              />
            </div>
            <button type="button" onClick={add.cancel} className="text-[13px] text-[var(--text-muted)]">
              Cancel
            </button>
          </>
        )}

        <p className="text-[11px] text-[var(--text-muted)] [text-wrap:pretty]">
          {add.accepted.map((extension) => extension.slice(1).toUpperCase()).join(" · ")}
        </p>
      </div>

      {add.added && (
        <p className="rounded-[var(--radius-md)] border border-[var(--success)] bg-[color-mix(in_oklch,var(--success)_12%,transparent)] p-3 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
          {addedName(add.added) ? (
            <>
              <span className="font-semibold text-[var(--text-primary)]">{addedName(add.added)}</span> is in.
            </>
          ) : (
            "That is in."
          )}{" "}
          The worker takes it from here — it appears below once it has been read.
        </p>
      )}

      {/* CAP-3. The original is named rather than linked: where it opens is a desktop
          screen, and a link that arrives at "this is desk work" is worse than a sentence
          that says which file it already has. */}
      {add.duplicate && (
        <p className="rounded-[var(--radius-md)] border border-[var(--warning)] bg-[color-mix(in_oklch,var(--warning)_12%,transparent)] p-3 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
          You already added this file
          {add.duplicate.title ? ` as “${add.duplicate.title}”` : ""} on {formatDate(add.duplicate.uploadedAt)}.
          Nothing was uploaded twice.
        </p>
      )}

      {add.problem && (
        <p role="alert" className="text-[13px] leading-[1.5] text-[var(--warning)] [text-wrap:pretty]">
          {add.problem}
        </p>
      )}

      <div className="flex flex-col gap-2.5">
        <p className="text-[11px] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          Or write it down
        </p>
        <textarea
          value={add.note}
          rows={5}
          aria-label="Write a note"
          placeholder="What happened, what was decided, what you want to remember…"
          onChange={(event) => add.setNote(event.target.value)}
          className="w-full resize-none rounded-[var(--radius-md)] border border-[var(--input)] bg-[var(--surface-glass)] p-3 text-[16px] leading-[1.5] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
        />
        <button
          type="button"
          disabled={add.saving || add.note.trim().length === 0}
          onClick={add.saveNote}
          className="h-12 w-full rounded-[var(--radius-md)] border border-[var(--border-default)] text-[15px] font-semibold text-[var(--text-primary)] disabled:opacity-50"
        >
          {add.saving ? "Saving…" : "Add note"}
        </button>
      </div>

      {/* CAP-5. The phone app has no settings tab and no room for a sixth, so the way to
          register a capture PC is here — beside the other answers to "how does a meeting
          get in", which is the question somebody is holding when they need it. */}
      <div className="flex flex-col gap-2">
        <p className="text-[11px] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          Or record on your PC
        </p>
        <Link
          to="/settings/capture"
          className="flex items-center gap-3 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-3 active:border-[var(--border-strong)]"
        >
          <Laptop size={18} strokeWidth={2} className="shrink-0 text-[var(--vl-bridge)]" aria-hidden />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[15px] font-bold text-[var(--text-primary)]">Set up a recorder</span>
            <span className="text-[12px] leading-[1.4] text-[var(--text-muted)] [text-wrap:pretty]">
              Factorium Recorder files recordings on its own. Name the PC and copy its token.
            </span>
          </span>
          <ChevronRight size={18} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" aria-hidden />
        </Link>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-[11px] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          Recently added
        </p>

        {recentSources.length === 0 ? (
          <p className="text-[13px] text-[var(--text-muted)]">Nothing yet.</p>
        ) : (
          recentSources.map((source) => {
            const recording = recordings.find((candidate) => candidate.id === source.id);
            const state = recording ? PIPELINE_STATE[recording.status ?? "queued"] : null;

            return (
              <div
                key={source.id}
                className="flex items-center gap-2.5 border-b border-[var(--border-subtle)] py-2.5"
              >
                <span
                  aria-hidden
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ background: state?.dot ?? "var(--text-muted)" }}
                />
                <span className="min-w-0 flex-1 truncate text-[14px] text-[var(--text-primary)]">
                  {source.title ?? source.originalFilename ?? "Untitled source"}
                </span>
                <span className="shrink-0 text-[11px] text-[var(--text-muted)]">
                  {/* A recording has a pipeline state worth reporting. A document or a note
                      has no run on this list, so its date is said instead of a state
                      invented for it. */}
                  {state?.label ?? formatDate(source.sourceDate ?? source.createdAt)}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
